import React, { useEffect, useState } from 'react'
import { useAdminExperience } from '../../../context/AdminExperienceContext'
import { useAccessContext } from '../../../context/AccessContext'
import { useI18n } from '../../../i18n/I18nContext'
import { updateAdminExperienceSettings } from '../../../services/access'
import type { AdminMode, GuidedWorkflowsMode } from '../../../types/access'

export const AdminExperienceSwitcher: React.FC = () => {
  const { t } = useI18n()
  const { mode, allowModeToggle, setMode, guidedEnabled, guidedRequired, guidedAvailable, setGuidedEnabled } = useAdminExperience()
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  if (!allowModeToggle && !guidedAvailable) return null

  const choose = async (nextMode: AdminMode) => {
    setSaving(true)
    setError('')
    try {
      await setMode(nextMode)
    } catch (cause: any) {
      setError(cause?.response?.data?.message || t('Unable to change the Admin experience.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="admin-experience-switcher" data-testid="admin-experience-switcher">
      <span className="helper">{t('Experience')}</span>
      {allowModeToggle ? <div className="segmented-control" role="group" aria-label={t('Admin experience mode')}>
        {(['simple','advanced'] as AdminMode[]).map((option) => (
          <button
            key={option}
            type="button"
            className={`btn ${mode === option ? 'btn-primary' : 'btn-secondary'}`}
            aria-pressed={mode === option}
            disabled={saving}
            onClick={() => choose(option)}
          >
            {t(option === 'simple' ? 'Simple' : 'Advanced')}
          </button>
        ))}
      </div> : null}
      {guidedAvailable ? <label className="admin-help-toggle"><input type="checkbox" checked={guidedEnabled} disabled={guidedRequired} onChange={(event) => setGuidedEnabled(event.target.checked)} /><span>{t('Guided')}</span>{guidedRequired ? <span className="helper">{t('Required')}</span> : null}</label> : null}
      {error ? <span className="form-error" role="alert">{error}</span> : null}
    </div>
  )
}

export const AdminExperienceSettingsPanel: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const { t } = useI18n()
  const { accessContext, refresh } = useAccessContext()
  const resolved = accessContext?.adminExperience
  const [defaultMode, setDefaultMode] = useState<AdminMode>(resolved?.defaultMode || 'simple')
  const [guidedWorkflows, setGuidedWorkflows] = useState<GuidedWorkflowsMode>(resolved?.guidedWorkflows || 'default_on')
  const [allowToggle, setAllowToggle] = useState(resolved?.allowAdvancedModeToggle ?? true)
  const [rememberPreference, setRememberPreference] = useState(resolved?.rememberUserPreference ?? true)
  const [status, setStatus] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!resolved) return
    setDefaultMode(resolved.defaultMode)
    setGuidedWorkflows(resolved.guidedWorkflows)
    setAllowToggle(resolved.allowAdvancedModeToggle)
    setRememberPreference(resolved.rememberUserPreference)
  }, [resolved])

  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setStatus('')
    try {
      await updateAdminExperienceSettings({
        defaultMode,
        guidedWorkflows,
        allowAdvancedModeToggle: allowToggle,
        rememberUserPreference: rememberPreference,
      })
      await refresh()
      setStatus(t('Admin experience settings saved.'))
    } catch (cause: any) {
      setStatus(cause?.response?.data?.message || t('Unable to save Admin experience settings.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="panel workspace-panel stack" onSubmit={save} data-testid="admin-experience-settings">
      <div className="stack" style={{ gap: '.25rem' }}>
        <h3 style={{ margin: 0 }}>{t('Admin experience')}</h3>
        <p className="text-muted">{t('Choose how operational complexity and guidance are presented. These settings never grant permissions.')}</p>
      </div>
      <label className="stack" style={{ gap: '.25rem' }}>
        <span>{t('Default experience')}</span>
        <select className="input" value={defaultMode} onChange={(event) => setDefaultMode(event.target.value as AdminMode)} disabled={!canManage || saving}>
          <option value="simple">{t('Simple')}</option>
          <option value="advanced">{t('Advanced')}</option>
        </select>
      </label>
      <label className="stack" style={{ gap: '.25rem' }}>
        <span>{t('Guided workflows')}</span>
        <select className="input" value={guidedWorkflows} onChange={(event) => setGuidedWorkflows(event.target.value as GuidedWorkflowsMode)} disabled={!canManage || saving}>
          <option value="disabled">{t('Disabled')}</option>
          <option value="available">{t('Available')}</option>
          <option value="default_on">{t('On by default')}</option>
          <option value="required">{t('Required')}</option>
        </select>
      </label>
      <label><input type="checkbox" checked={allowToggle} onChange={(event) => setAllowToggle(event.target.checked)} disabled={!canManage || saving} /> {t('Allow authorized users to switch modes')}</label>
      <label><input type="checkbox" checked={rememberPreference} onChange={(event) => setRememberPreference(event.target.checked)} disabled={!canManage || saving} /> {t('Remember each user’s preference')}</label>
      {canManage ? <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? t('Saving…') : t('Save experience settings')}</button> : <p className="helper">{t('You can view these settings, but tenant settings permission is required to change them.')}</p>}
      {status ? <p className={status.includes('Unable') ? 'form-error' : 'helper'} role="status">{status}</p> : null}
    </form>
  )
}
